/**
 * Helper utilities for working with ArrowFunctionShape expression bodies.
 *
 * The SDK's ArrowFunctionPlugin stores body content in getStatements(),
 * but for expression-body arrow functions like `(x) => expr`, the ts-morph
 * node's getStatements() returns []. This module provides fallback helpers
 * that access the underlying ts-morph AST node to extract the expression body.
 */

import { ts } from '@servicenow/sdk-build-core'
import type { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'

/**
 * Get the ts-morph body node of an expression-body ArrowFunctionShape.
 * Returns undefined if the shape has statements (block body) or if the source is unavailable.
 */
// biome-ignore lint/suspicious/noExplicitAny: returns ts-morph node
function getArrowBodyNode(shape: ArrowFunctionShape): any | undefined {
    if (shape.getStatements().length > 0) {
        return undefined // Block body with statements - handled normally
    }
    try {
        // biome-ignore lint/suspicious/noExplicitAny: accessing ts-morph source node
        const source = (shape as any).getSource?.() ?? (shape as any).getOriginalSource?.()
        if (source && ts.Node.isArrowFunction(source)) {
            const body = source.getBody()
            // Only return for expression bodies (not Block)
            if (body && !ts.Node.isBlock(body)) {
                return body
            }
        }
    } catch {
        // Silently fail
    }
    return undefined
}

/**
 * Get the expression body text of an ArrowFunctionShape.
 * Returns undefined if the shape has statements (block body) or if the source is unavailable.
 */
export function getArrowExpressionBodyText(shape: ArrowFunctionShape): string | undefined {
    const body = getArrowBodyNode(shape)
    if (body && typeof body.getText === 'function') {
        return body.getText()
    }
    return undefined
}

/**
 * Extract property name from expression-body arrow like `(targets) => targets.ToolName`.
 * Returns the last property name (e.g., 'ToolName').
 */
export function extractPropertyNameFromArrowBody(shape: ArrowFunctionShape): string | undefined {
    const bodyText = getArrowExpressionBodyText(shape)
    if (!bodyText) {
        return undefined
    }

    // Match patterns like targets.ToolName or x.y.z (last element)
    const match = bodyText.match(/\.(\w+)\s*$/)
    return match?.[1]
}

/**
 * Extract branch definitions from an expression-body arrow function.
 * Handles: `(targets) => [{ name: '...', to: targets.ToolA, condition: ... }, ...]`
 *
 * Walks the ts-morph AST to extract branch objects from the array literal.
 */
export function extractBranchesFromArrowBody(
    shape: ArrowFunctionShape,
    toolNameToVarName: Map<string, string>
): Array<{ name: string; target: string; condition: string; conditionType: 'filter' | 'script' }> | undefined {
    const body = getArrowBodyNode(shape)
    if (!body || !ts.Node.isArrayLiteralExpression(body)) {
        return undefined
    }

    const branches: Array<{ name: string; target: string; condition: string; conditionType: 'filter' | 'script' }> = []

    for (const element of body.getElements()) {
        if (!ts.Node.isObjectLiteralExpression(element)) {
            continue
        }

        let branchName = ''
        let target: string | undefined
        let conditionStr = ''
        let conditionType: 'filter' | 'script' = 'filter'

        for (const prop of element.getProperties()) {
            if (!ts.Node.isPropertyAssignment(prop)) {
                continue
            }

            const propName = prop.getName()
            const initializer = prop.getInitializer()
            if (!initializer) {
                continue
            }

            switch (propName) {
                case 'name':
                    if (ts.Node.isStringLiteral(initializer)) {
                        branchName = initializer.getLiteralValue()
                    }
                    break
                case 'to':
                case 'target':
                    if (!target) {
                        if (ts.Node.isPropertyAccessExpression(initializer)) {
                            target = initializer.getName()
                        } else if (ts.Node.isStringLiteral(initializer)) {
                            target = initializer.getLiteralValue()
                        }
                    }
                    break
                case 'condition':
                    if (ts.Node.isStringLiteral(initializer)) {
                        conditionStr = initializer.getLiteralValue()
                    } else if (ts.Node.isObjectLiteralExpression(initializer)) {
                        const result = extractConditionFromObjectLiteral(initializer, toolNameToVarName)
                        conditionStr = result.condition
                        conditionType = result.conditionType
                    }
                    break
            }
        }

        if (target) {
            branches.push({ name: branchName, target, condition: conditionStr, conditionType })
        }
    }

    return branches.length > 0 ? branches : undefined
}

/**
 * Extract a condition expression from a ts-morph ObjectLiteralExpression.
 * Handles: { field: t.input.category, operator: 'is', value: 'hardware' }
 *          { script: '...' }
 *          { expression: '...' }
 */
// biome-ignore lint/suspicious/noExplicitAny: ts-morph node
function extractConditionFromObjectLiteral(
    obj: any,
    toolNameToVarName: Map<string, string>
): { condition: string; conditionType: 'filter' | 'script' } {
    const props: Record<string, string> = {}

    for (const prop of obj.getProperties()) {
        if (!ts.Node.isPropertyAssignment(prop)) {
            continue
        }
        const name = prop.getName()
        const init = prop.getInitializer()
        if (!init) {
            continue
        }

        if (ts.Node.isStringLiteral(init)) {
            props[name] = init.getLiteralValue()
        } else if (ts.Node.isNumericLiteral(init)) {
            props[name] = init.getLiteralValue().toString()
        } else if (ts.Node.isPropertyAccessExpression(init)) {
            // Handle t.input.fieldName or toolVar.output patterns
            const text = init.getText()
            if (text.includes('.input.')) {
                const parts = text.split('.')
                const fieldName = parts[parts.length - 1] ?? ''
                props[name] = fieldName
            } else {
                // Try to resolve tool variable name to tool name
                const parts = text.split('.')
                const varName = parts[0] ?? ''
                let resolvedName = varName
                for (const [toolName, vName] of toolNameToVarName) {
                    if (vName === varName) {
                        resolvedName = toolName
                        break
                    }
                }
                props[name] = `${resolvedName}.${parts.slice(1).join('.')}`
            }
        }
    }

    // Script-based condition
    if (props.script) {
        return { condition: props.script, conditionType: 'script' }
    }
    // Expression-based condition
    if (props.expression) {
        return { condition: props.expression, conditionType: 'script' }
    }
    // Field-based condition: { field, operator, value }
    if (props.field && props.operator) {
        const platformOperator = props.operator === 'is' ? '=' : props.operator === 'is_not' ? '!=' : props.operator
        // Strip existing {{}} if present to avoid double-wrapping
        const fieldValue =
            props.field.startsWith('{{') && props.field.endsWith('}}') ? props.field.slice(2, -2) : props.field
        return { condition: `{{${fieldValue}}}${platformOperator}${props.value ?? ''}`, conditionType: 'filter' }
    }
    return { condition: '', conditionType: 'filter' }
}
